// lib/utils/tax-calculation.ts
// 양도소득세 계산 유틸리티 (Policy 기반)
// Version: 2.2
// Last Updated: 2025-01-28

import { Policy } from "@/lib/core/policy/policy";
import defaultPolicy from "@/lib/core/policy/defaultpolicy";

/**
 * 양도소득세 계산 결과
 */
export interface TransferTaxResult {
  /** 적용 세율 (0-1 범위) */
  taxRate: number;
  /** 예상 납부세액 */
  taxAmount: number;
  /** 세후 순이익 */
  afterTaxProfit: number;
  /** 보유기간 (개월) */
  holdingPeriodMonths: number;
  /** Plan 타입 (A/B/C) */
  planType: "A" | "B" | "C";
}

/**
 * 양도소득세 계산
 * 
 * @param transferGain 양도차익 (세전 순수익)
 * @param holdingPeriodMonths 보유기간 (개월)
 * @param policy 정책 (기본값: defaultPolicy)
 * @returns 양도소득세 계산 결과
 */
export function calculateTransferTax(
  transferGain: number,
  holdingPeriodMonths: number,
  policy: Policy = defaultPolicy,
): TransferTaxResult {
  const transferTaxPolicy = policy.cost.transferTax || defaultPolicy.cost.transferTax!;
  const { ratesByHoldingPeriod } = transferTaxPolicy;

  let taxRate: number;
  let planType: "A" | "B" | "C";

  // 보유기간에 따른 세율 결정
  if (holdingPeriodMonths < 12) {
    // 1년 미만: Plan A (중과세)
    taxRate = ratesByHoldingPeriod.underOneYear;
    planType = "A";
  } else if (holdingPeriodMonths <= 24) {
    // 1년 이상 2년 이하 (12~24개월): Plan B (기본세율)
    taxRate = ratesByHoldingPeriod.oneToTwoYears;
    planType = "B";
  } else {
    // 2년 초과 (25개월 이상): Plan C (비과세)
    taxRate = ratesByHoldingPeriod.overTwoYears;
    planType = "C";
  }

  // 세액 계산
  const taxAmount = transferGain * taxRate;
  const afterTaxProfit = transferGain - taxAmount;

  return {
    taxRate,
    taxAmount,
    afterTaxProfit,
    holdingPeriodMonths,
    planType,
  };
}

/**
 * 여러 보유기간에 대한 양도소득세 시뮬레이션
 * 
 * @param transferGain 양도차익 (세전 순수익)
 * @param holdingPeriods 보유기간 배열 (개월)
 * @param policy 정책 (기본값: defaultPolicy)
 * @returns 각 보유기간별 양도소득세 계산 결과
 */
export function simulateTransferTaxScenarios(
  transferGain: number,
  holdingPeriods: number[],
  policy: Policy = defaultPolicy,
): TransferTaxResult[] {
  return holdingPeriods.map((months) =>
    calculateTransferTax(transferGain, months, policy),
  );
}

